import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../features/auth/store";
import { IS_API_MODE } from "../../shared/config/runtime";
import { createPostComment, getCursorFromPageUrl, listPosts, setPostCollection, setPostLike } from "../../entities/post/api";
import { getApiErrorMessage } from "../../shared/api/errors";
import { invalidatePostQueries, restorePostCaches, updatePostCaches } from "../../features/post-engagement/postCache";
import { adaptPost, normalizeCursorPage } from "../../entities/post/adapter";
import { getFollowingUsers } from "../../entities/user/api";
import { initialPosts, stories } from "../../shared/data/mockData";
import PostCard from "../../widgets/PostFeed/PostCard";
import RightRail from "../../widgets/PostFeed/RightRail";
import defaultAvatar from "../../shared/ui/defaultAvatar";

export default function FeedPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const currentUser = useAuthStore((state) => state.user);
  const [posts, setPosts] = useState(IS_API_MODE ? [] : initialPosts);
  const [activeTab, setActiveTab] = useState("recommended");
  const [actionError, setActionError] = useState("");
  const postsQuery = useInfiniteQuery({
    queryKey: ["posts", "feed"],
    queryFn: ({ pageParam }) => listPosts({ cursor: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => getCursorFromPageUrl(lastPage?.next),
    enabled: IS_API_MODE,
  });
  const followingQuery = useQuery({
    queryKey: ["users", currentUser?.id, "following"],
    queryFn: () => getFollowingUsers(currentUser.id),
    enabled: IS_API_MODE && isAuthenticated && Boolean(currentUser?.id),
    staleTime: 60_000,
  });

  const followingStories = IS_API_MODE
    ? (followingQuery.data?.results || followingQuery.data || []).map((user) => ({
        id: String(user.id),
        name: user.nickname || user.username,
        avatar: user.avatar_url || user.avatarUrl || defaultAvatar,
      }))
    : stories;
  const followingUsers = IS_API_MODE
    ? (followingQuery.data?.results || followingQuery.data || [])
    : stories;
  const followingIds = useMemo(
    () => new Set(followingUsers.map((item) => String(item.id))),
    [followingUsers],
  );
  const visiblePosts = useMemo(() => {
    if (activeTab !== "following") return posts;
    return posts.filter((post) => followingIds.has(String(post.authorId || post.author_id)));
  }, [activeTab, followingIds, posts]);

  useEffect(() => {
    if (!postsQuery.data) return;
    const remotePosts = postsQuery.data.pages.flatMap(
      (page) => normalizeCursorPage(page).items,
    );
    setPosts(remotePosts.map(adaptPost));
  }, [postsQuery.data]);

  async function toggleLike(id) {
    if (IS_API_MODE && !isAuthenticated) {
      navigate("/login", { state: { from: "/" } });
      return;
    }
    const current = posts.find((post) => post.id === id);
    if (!current) return;
    const nextLiked = !current.liked;
    const previousCaches = queryClient.getQueriesData({ queryKey: ["posts"] });
    await queryClient.cancelQueries({ queryKey: ["posts"] });
    setPosts((items) =>
      items.map((post) =>
        post.id === id
          ? { ...post, liked: !post.liked, likes: post.likes + (post.liked ? -1 : 1) }
          : post,
      ),
    );
    updatePostCaches(queryClient, id, {
      is_liked: nextLiked,
      liked: nextLiked,
      like_count: current.likes + (nextLiked ? 1 : -1),
    });
    if (!IS_API_MODE) return;
    try {
      await setPostLike(id, nextLiked);
      await invalidatePostQueries(queryClient);
    } catch (error) {
      restorePostCaches(queryClient, previousCaches);
      setPosts((items) =>
        items.map((post) =>
          post.id === id
            ? { ...post, liked: current.liked, likes: current.likes }
            : post,
        ),
      );
      setActionError(getApiErrorMessage(error, "点赞操作失败"));
    }
  }

  async function toggleSave(id) {
    if (IS_API_MODE && !isAuthenticated) {
      navigate("/login", { state: { from: "/" } });
      return;
    }
    const current = posts.find((post) => post.id === id);
    if (!current) return;
    const nextSaved = !current.saved;
    const previousCaches = queryClient.getQueriesData({ queryKey: ["posts"] });
    await queryClient.cancelQueries({ queryKey: ["posts"] });
    setPosts((items) =>
      items.map((post) => (post.id === id ? { ...post, saved: !post.saved } : post)),
    );
    updatePostCaches(queryClient, id, {
      is_collected: nextSaved,
      saved: nextSaved,
    });
    if (!IS_API_MODE) return;
    try {
      await setPostCollection(id, nextSaved);
      await invalidatePostQueries(queryClient);
    } catch (error) {
      restorePostCaches(queryClient, previousCaches);
      setPosts((items) =>
        items.map((post) =>
          post.id === id ? { ...post, saved: current.saved } : post,
        ),
      );
      setActionError(getApiErrorMessage(error, "收藏操作失败"));
    }
  }

  async function addComment(postId, content, rollback) {
    if (IS_API_MODE && !isAuthenticated) {
      rollback?.();
      navigate("/login", { state: { from: "/" } });
      return;
    }
    setPosts((items) =>
      items.map((post) =>
        post.id === postId ? { ...post, comments: post.comments + 1 } : post,
      ),
    );
    const previousCaches = queryClient.getQueriesData({ queryKey: ["posts"] });
    await queryClient.cancelQueries({ queryKey: ["posts"] });
    const current = posts.find((post) => post.id === postId);
    updatePostCaches(queryClient, postId, {
      comment_count: (current?.comments || 0) + 1,
    });
    if (!IS_API_MODE) return;
    try {
      await createPostComment(postId, content);
      await invalidatePostQueries(queryClient);
    } catch (error) {
      restorePostCaches(queryClient, previousCaches);
      rollback?.();
      setPosts((items) =>
        items.map((post) =>
          post.id === postId
            ? { ...post, comments: Math.max(0, post.comments - 1) }
            : post,
        ),
      );
      setActionError(getApiErrorMessage(error, "评论发布失败"));
    }
  }

  function selectFeedTab(tab) {
    if (tab === "following" && IS_API_MODE && !isAuthenticated) {
      navigate("/login", { state: { from: "/" } });
      return;
    }
    setActiveTab(tab);
  }

  return (
    <div className="feed-layout">
      <section className="feed-column">
        <div className="feed-tabs">
          <button className={activeTab === "recommended" ? "active" : ""} onClick={() => selectFeedTab("recommended")}>为你推荐</button>
          <button className={activeTab === "following" ? "active" : ""} onClick={() => selectFeedTab("following")}>正在关注</button>
        </div>
        {postsQuery.isPending && IS_API_MODE && (
          <div className="feed-state">正在加载设计作品……</div>
        )}
        {postsQuery.isError && (
          <div className="feed-state error">
            <span>{getApiErrorMessage(postsQuery.error)}</span>
            <button onClick={() => postsQuery.refetch()}>重新加载</button>
          </div>
        )}
        {actionError && (
          <div className="action-error">
            <span>{actionError}</span>
            <button onClick={() => setActionError("")} aria-label="关闭错误提示">
              <X size={16} />
            </button>
          </div>
        )}
        {activeTab === "following" && followingQuery.isPending && (
          <div className="feed-state">正在加载关注用户的作品……</div>
        )}
        {!postsQuery.isPending && activeTab === "following" && !followingQuery.isPending && visiblePosts.length === 0 && (
          <div className="feed-state">暂无关注用户发布的作品。</div>
        )}
        {!postsQuery.isPending && activeTab === "recommended" && visiblePosts.length === 0 && (
          <div className="feed-state">暂时没有可展示的帖子。</div>
        )}
        {visiblePosts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            onToggleLike={toggleLike}
            onToggleSave={toggleSave}
            onAddComment={addComment}
          />
        ))}
        {postsQuery.hasNextPage && (
          <button
            className="load-more-button"
            disabled={postsQuery.isFetchingNextPage}
            onClick={() => postsQuery.fetchNextPage()}
          >
            {postsQuery.isFetchingNextPage ? "正在加载更多作品……" : "加载更多作品"}
          </button>
        )}
      </section>
      <RightRail posts={posts} followingUsers={followingStories} />
    </div>
  );
}
